import { deleteData, getData, patchData, postData } from "../api/api";
import type { ResourceQueryCallPage } from "../components/ResourceQueryStatsPanel";
import { QUERY_HISTORY_PAGE_SIZE } from "../components/ResourceQueryStatsPanel";

export type DataAdaptorType =
  | "POSTGRESQL"
  | "MYSQL"
  | "MONGODB"
  | "TRINO"
  | "CLICKHOUSE"
  | "HIVE"
  | "SPARK_SQL"
  | "DATABRICKS"
  | "SNOWFLAKE"
  | "BIGQUERY"
  | "REDSHIFT";

export const WAREHOUSE_ADAPTOR_TYPES: DataAdaptorType[] = [
  "TRINO",
  "CLICKHOUSE",
  "HIVE",
  "SPARK_SQL",
  "DATABRICKS",
  "SNOWFLAKE",
  "BIGQUERY",
  "REDSHIFT",
];

/** Big data engines: read-only, scan-limit guarded. */
export const isWarehouseAdaptorType = (type?: DataAdaptorType | null): boolean =>
  !!type && WAREHOUSE_ADAPTOR_TYPES.includes(type);

/** How the password / key fields authenticate a warehouse adaptor. */
export type AdaptorAuthMode = "PASSWORD" | "TOKEN" | "OAUTH_M2M" | "KEY_PAIR" | "SERVICE_ACCOUNT_JSON" | "KERBEROS";

/** Warehouse runtime / cost settings shared by the adaptor response and save request. */
export type WarehouseRuntimeSettings = {
  /** null = server default, 0 = never cache. */
  resultCacheTtlSeconds?: number | null;
  queryTimeoutSeconds?: number | null;
  dailyScanBytesLimit?: number | null;
  dailyQueryLimit?: number | null;
  maxConcurrentAiQueries?: number | null;
  resourceGroup?: string | null;
  requirePartitionFilter?: boolean | null;
  ragSampleValues?: boolean | null;
  allowEngineMl?: boolean | null;
  kerberosServicePrincipal?: string | null;
};

export type DataAdaptor = WarehouseRuntimeSettings & {
  id: string;
  name: string;
  type: DataAdaptorType;
  enabled: boolean;
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  sslMode?: string;
  authDatabase?: string;
  vpnProfileId?: string | null;
  vpnProfileName?: string | null;
  passwordSet?: boolean;
  connectionUriSet?: boolean;
  /** Host:port from the stored Mongo URI; never includes credentials. */
  connectionUriHosts?: string | null;
  catalog?: string | null;
  allowedSchemas?: string[] | null;
  maxScanBytes?: number | null;
  maxScanRows?: number | null;
  maxResultRows?: number | null;
  authMode?: AdaptorAuthMode | null;
  /** A BigQuery service-account JSON / Snowflake private key is stored (never returned). */
  keyJsonSet?: boolean;
  httpPath?: string | null;
  computeWarehouse?: string | null;
  role?: string | null;
  extraJdbcProps?: Record<string, string> | null;
  queryCallCount?: number;
  querySuccessCount?: number;
  queryFailureCount?: number;
  autoDisableEnabled?: boolean;
  autoDisableMinSuccessPercent?: number | null;
  autoDisableMinCalls?: number | null;
  queryStatsResetAt?: string | null;
  autoDisabledAt?: string | null;
  createdDate?: string;
  lastModifiedDate?: string;
};

export type DataAdaptorSaveRequest = WarehouseRuntimeSettings & {
  name?: string;
  type?: DataAdaptorType;
  enabled?: boolean;
  host?: string;
  port?: number | null;
  database?: string;
  username?: string;
  password?: string;
  connectionUri?: string;
  sslMode?: string;
  authDatabase?: string;
  /** VPN profile id; "" clears the assignment, undefined leaves it unchanged. */
  vpnProfileId?: string;
  /** Warehouse adaptors only. "" clears the catalog. */
  catalog?: string;
  allowedSchemas?: string[];
  /** Warehouse adaptors only. 0 clears the limit, undefined leaves it unchanged. */
  maxScanBytes?: number;
  maxScanRows?: number;
  maxResultRows?: number;
  authMode?: AdaptorAuthMode;
  /** Write-only; blank keeps the stored key. */
  keyJson?: string;
  /** "" clears. */
  httpPath?: string;
  computeWarehouse?: string;
  role?: string;
  /** Non-secret driver / client properties; {} clears. */
  extraJdbcProps?: Record<string, string>;
  autoDisableEnabled?: boolean;
  autoDisableMinSuccessPercent?: number;
  autoDisableMinCalls?: number;
};

export type DataAdaptorTestResult = {
  ok: boolean;
  message: string;
  /** Present only when the adaptor uses a VPN profile. */
  vpnOk?: boolean | null;
  vpnMessage?: string | null;
};

export type DataAdaptorSummary = {
  id: string;
  name: string;
  type: DataAdaptorType;
};

export type AdaptorObjectNode = {
  name: string;
  kind?: string;
  schema?: string;
  qualifiedName?: string;
  children?: AdaptorObjectNode[];
};

export type AdaptorColumnInfo = {
  name: string;
  dataType?: string;
  nullable?: boolean;
  defaultValue?: string;
  notes?: string;
  /** Warehouse partition column (badge in the schema browser). */
  partitionKey?: boolean | null;
};

export type AdaptorObjectDescribe = {
  qualifiedName: string;
  columns?: AdaptorColumnInfo[];
};

export type AdaptorMetadataTree = {
  adaptorId: string;
  adaptorName?: string;
  adaptorType?: string;
  roots?: AdaptorObjectNode[];
};

/** Flatten metadata tree to selectable tables/collections (skips SCHEMA parents). */
export function flattenAdaptorLeafObjects(nodes: AdaptorObjectNode[] | undefined | null): AdaptorObjectNode[] {
  if (!nodes?.length) return [];
  const leaves: AdaptorObjectNode[] = [];
  const walk = (list: AdaptorObjectNode[]) => {
    for (const node of list) {
      const kind = (node.kind || "").toUpperCase();
      if (kind === "SCHEMA" || (node.children && node.children.length > 0 && kind !== "TABLE" && kind !== "COLLECTION")) {
        walk(node.children || []);
        continue;
      }
      if (kind === "TABLE" || kind === "COLLECTION" || (!kind && (node.qualifiedName || node.name))) {
        leaves.push(node);
        continue;
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return leaves;
}

export const adminDataAdaptorService = {
  list: () => getData("/admin/data-adaptors") as Promise<DataAdaptor[]>,
  get: (id: string) => getData(`/admin/data-adaptors/${id}`) as Promise<DataAdaptor>,
  create: (body: DataAdaptorSaveRequest) =>
    postData("/admin/data-adaptors", body) as Promise<DataAdaptor>,
  update: (id: string, body: DataAdaptorSaveRequest) =>
    patchData(`/admin/data-adaptors/${id}`, body) as Promise<DataAdaptor>,
  remove: (id: string) => deleteData(`/admin/data-adaptors/${id}`),
  test: (id: string) =>
    postData(`/admin/data-adaptors/${id}/test`, {}) as Promise<DataAdaptorTestResult>,
  testWithRequest: (body: DataAdaptorSaveRequest) =>
    postData("/admin/data-adaptors/test", body) as Promise<DataAdaptorTestResult>,
  metadataTree: (id: string) =>
    getData(`/admin/data-adaptors/${id}/metadata/tree`) as Promise<AdaptorMetadataTree>,
  describeObject: (id: string, name: string) =>
    getData(
      `/admin/data-adaptors/${id}/metadata/objects/${encodeURIComponent(name)}`
    ) as Promise<AdaptorObjectDescribe>,
  queryCalls: (id: string, page = 0, size = QUERY_HISTORY_PAGE_SIZE) =>
    getData(`/admin/data-adaptors/${id}/query-calls?page=${page}&size=${size}`) as Promise<ResourceQueryCallPage>,
  resetQueryStats: (id: string) =>
    postData(`/admin/data-adaptors/${id}/query-stats/reset`, {}) as Promise<DataAdaptor>,
};
